/**
 * Hooks do módulo Planos de Ação (GUPM-187) — server state via TanStack Query.
 * A UI só fala com estes hooks; nunca com Supabase direto (regra #2).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { diaEmBrasilia } from '@/domain/fuso-brasilia';
import { pollingQueryOptions } from '@/lib/query-polling';
import type { AcaoStatus } from '@/domain/planos';
import { useDataSources, type CreatePlanoInput } from '@/services/datasources';

const PLANOS_ROOT = ['planos'] as const;

/** Com que frequência a tela confere se o dia de Brasília virou. */
const CONFERE_DIA_MS = 60 * 1000;

/**
 * "Hoje" da carteira, que acompanha a VIRADA DO DIA de Brasília com a tela aberta
 * (GUPM-332). Fixá-lo na montagem fazia uma tela deixada aberta na sala de controle
 * seguir no dia anterior depois da meia-noite — ação vencida sem aparecer como tal.
 *
 * Só troca de valor quando o DIA muda: dentro do mesmo dia o objeto é estável, então
 * contagens e filtros de prazo não recalculam a cada minuto.
 */
export function useHoje(): Date {
  const [hoje, setHoje] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => {
      const agora = new Date();
      setHoje((atual) => (diaEmBrasilia(agora) === diaEmBrasilia(atual) ? atual : agora));
    }, CONFERE_DIA_MS);
    return () => clearInterval(id);
  }, []);
  return hoje;
}

export function usePlanos() {
  const { planos } = useDataSources();
  return useQuery({
    queryKey: [...PLANOS_ROOT, 'list'],
    queryFn: () => planos.listPlanos(),
    ...pollingQueryOptions,
  });
}

export function usePlanoMutations() {
  const { planos } = useDataSources();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: PLANOS_ROOT });

  const updateAcao = useMutation({
    mutationFn: (vars: { planoId: string; acaoId: string; status: AcaoStatus }) =>
      planos.updateAcaoStatus(vars.planoId, vars.acaoId, vars.status),
    onSuccess: invalidate,
  });

  return { updateAcao };
}

/** Criação manual de plano (GUPM-193) — invalida a carteira ao concluir. */
export function useCreatePlano() {
  const { planos } = useDataSources();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePlanoInput) => planos.createPlano(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: PLANOS_ROOT }),
  });
}

/**
 * Lista de instrumentos cadastrados (GUPM-193) — alimenta o dropdown de instrumento
 * do "Novo Plano" e resolve as coordenadas (UTM 23S) pra auto-preencher o local.
 * Namespace próprio (`planos-novo`) pra não ser invalidado pelo root `['planos']`.
 */
export function useInstrumentos() {
  const { instrumentacao } = useDataSources();
  return useQuery({
    queryKey: ['planos-novo', 'instrumentos'],
    queryFn: () => instrumentacao.listInstruments(),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * TARPs às quais um plano manual pode se associar (GUPM-213) — dropdown "TARP
 * associada". Inclui ABERTA + o pipeline de fechamento do RT (CONCLUIDA_AGUARDANDO/
 * EM_ANALISE_RT) + ENCERRADA; só o rascunho EM_ABERTURA fica de fora (ainda não é uma
 * TARP de fato). Selecionar uma TARP de instrumento herda barragem+instrumento.
 */
export function useTarpsVinculaveis() {
  const { tarp } = useDataSources();
  return useQuery({
    queryKey: ['planos-novo', 'tarps-vinculaveis'],
    queryFn: () =>
      tarp.listTarps({ status: ['ABERTA', 'CONCLUIDA_AGUARDANDO', 'EM_ANALISE_RT', 'ENCERRADA'] }),
    staleTime: 60 * 1000,
  });
}
